import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  BarChart3,
  Users,
  CreditCard,
  TrendingUp,
  Bell,
  AlertTriangle,
  Trash2,
  Send,
  Plus,
  Eye,
  ScanLine,
  Mail,
  Loader2,
  Quote,
  EyeOff,
} from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = ["Resumen", "Embudo", "Scans", "Novedades", "Testimonios", "Usuarios", "Pagos"];

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("Resumen");
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [scans, setScans] = useState(null);
  const [scanFilter, setScanFilter] = useState({ onlyWithEmail: false, domain: "" });
  const [novedades, setNovedades] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: "200",
        only_with_email: scanFilter.onlyWithEmail ? "true" : "false",
        ...(scanFilter.domain ? { domain: scanFilter.domain } : {}),
      });
      const [s, u, p, sc, n, t] = await Promise.all([
        axios.get(`${API}/admin/stats?days=${days}`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/pagos`),
        axios.get(`${API}/admin/scans?${params}`),
        axios.get(`${API}/novedades`),
        axios.get(`${API}/admin/testimonials`),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setPagos(p.data);
      setScans(sc.data);
      setNovedades(n.data);
      setTestimonials(t.data);
    } catch {
      toast.error("Error al cargar el dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, [days, scanFilter.onlyWithEmail, scanFilter.domain]);

  if (!user) return null;

  return (
    <>
      <SEO title="Super Admin" description="Panel de gestión interno" path="/admin" noindex />
      <section className="px-6 py-10 sm:py-14 bg-white min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Operating-style header */}
          <div className="mb-10 border-b border-slate-200 pb-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-3">
                  <span className="live-dot" />
                  Operating dashboard · Super Admin
                </div>
                <h1 className="font-display text-4xl sm:text-5xl text-slate-900 leading-tight tracking-tight">
                  Panel de <em className="italic text-blue-700">control</em>
                </h1>
                <p className="mt-2 text-sm font-mono text-slate-500">
                  {user.email} · sesión segura · v4.0
                </p>
              </div>
              <select
                data-testid="days-filter"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="h-11 px-4 bg-white border border-slate-300 rounded-xl text-sm font-medium hover:border-blue-600 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all"
              >
                <option value={7}>Últimos 7 días</option>
                <option value={30}>Últimos 30 días</option>
                <option value={90}>Últimos 90 días</option>
                <option value={365}>Último año</option>
              </select>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-slate-50 p-1 rounded-2xl border border-slate-200 mb-10 overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t}
                data-testid={`tab-${t.toLowerCase()}`}
                onClick={() => setTab(t)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                  tab === t ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-white"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid place-items-center py-20"><Loader2 className="w-7 h-7 text-[#1E3A8A] animate-spin" /></div>
          ) : (
            <>
              {tab === "Resumen" && <Resumen stats={stats} />}
              {tab === "Embudo" && <Embudo stats={stats} />}
              {tab === "Scans" && (
                <ScansTable
                  scans={scans}
                  filter={scanFilter}
                  onFilter={setScanFilter}
                />
              )}
              {tab === "Novedades" && <Novedades novedades={novedades} onReload={fetchAll} />}
              {tab === "Testimonios" && <TestimoniosTab items={testimonials} onReload={fetchAll} />}
              {tab === "Usuarios" && <UsersTable users={users} />}
              {tab === "Pagos" && <PagosTable pagos={pagos} />}
            </>
          )}
        </div>
      </section>
    </>
  );
}

function Resumen({ stats }) {
  if (!stats) return null;
  const t = stats.totales;
  const a = stats.actividad_periodo;
  const p = stats.pagos;
  const r = stats.retencion;
  return (
    <div className="space-y-10">
      {/* Group 1: USUARIOS */}
      <div>
        <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-4">
          ▸ Usuarios
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric icon={Users} label="Usuarios totales" value={t.usuarios} />
          <Metric icon={Users} label="Pro activos" value={t.pro_activos} tone="emerald" />
          <Metric icon={Users} label="Expirados" value={t.expirados} tone="amber" />
          <Metric icon={TrendingUp} label="Nuevos en período" value={t.nuevos_usuarios_periodo} />
        </div>
      </div>

      {/* Group 2: ACTIVIDAD */}
      <div>
        <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-4">
          ▸ Actividad del período
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric icon={Eye} label="Visitas" value={a.pageviews} />
          <Metric icon={ScanLine} label="Escaneos" value={a.scans} />
          <Metric icon={Mail} label="Emails capturados" value={a.scans_con_email} />
          <Metric icon={BarChart3} label="Documentos generados" value={a.documentos_generados} />
        </div>
      </div>

      {/* Group 3: REVENUE */}
      <div>
        <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-4">
          ▸ Revenue · Retención
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Metric icon={CreditCard} label="Ingreso del período" value={`$${p.ingreso_periodo_clp.toLocaleString("es-CL")}`} highlight />
          <Metric icon={CreditCard} label="Ingreso total CLP" value={`$${p.ingreso_total_clp.toLocaleString("es-CL")}`} />
          <Metric icon={TrendingUp} label="Tasa de retención" value={`${r.tasa_retencion_pct}%`} tone="emerald" />
        </div>
      </div>

      {stats.pageviews_top?.length > 0 && (
        <div>
          <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-4">
            ▸ Pageviews · Top paths
          </p>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <ul className="divide-y divide-slate-100">
              {stats.pageviews_top.map((pv, idx) => (
                <li key={pv.path} className="flex items-center justify-between px-5 py-3 text-sm hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] text-slate-400 tabular-nums w-6">{String(idx + 1).padStart(2, "0")}</span>
                    <code className="font-mono text-slate-700">{pv.path}</code>
                  </div>
                  <span className="font-display text-lg font-bold text-slate-900 tabular-nums">{pv.count}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function Embudo({ stats }) {
  if (!stats?.embudo) return null;
  const f = stats.embudo;
  const steps = [
    { label: "Visitas", value: f.visitas },
    { label: "Escaneos", value: f.escaneos },
    { label: "Emails capturados", value: f.emails_capturados },
    { label: "Pagos", value: f.pagos },
    { label: "Renovaciones", value: f.renovaciones },
  ];
  const max = Math.max(...steps.map((s) => s.value), 1);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-200 bg-slate-50/60">
        <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase">
          ▸ Embudo de conversión
        </p>
        <h3 className="mt-1 font-display text-xl text-slate-900 tracking-tight">
          Visitas → Escaneos → Emails → Pagos → Renovaciones
        </h3>
      </div>
      <div className="p-6 space-y-4">
        {steps.map((s, i) => {
          const w = Math.max((s.value / max) * 100, 4);
          return (
            <div key={s.label}>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="font-mono text-[10px] text-slate-500 uppercase tracking-wider">
                  {String(i + 1).padStart(2, "0")} · {s.label}
                </span>
                <span className="font-display font-bold text-slate-900 tabular-nums">{s.value.toLocaleString("es-CL")}</span>
              </div>
              <div className="h-7 bg-slate-100 rounded-lg overflow-hidden">
                <div
                  className="h-full rounded-lg transition-all bg-gradient-to-r from-blue-700 via-blue-500 to-blue-300"
                  style={{ width: `${w}%` }}
                />
              </div>
            </div>
          );
        })}
        <div className="pt-6 mt-2 border-t border-slate-100 grid grid-cols-2 gap-3">
          <ConvRow label="Escaneo → Email" value={f.conv_scan_email} />
          <ConvRow label="Email → Pago" value={f.conv_email_pago} />
        </div>
      </div>
    </div>
  );
}

function ConvRow({ label, value }) {
  return (
    <div className="px-4 py-4 rounded-xl bg-blue-50/60 border border-blue-100">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-blue-700">{label}</p>
      <p className="mt-2 font-display text-3xl font-bold text-slate-900 tabular-nums leading-none">{value}<span className="text-base text-slate-500 ml-1">%</span></p>
    </div>
  );
}

function Metric({ icon: Icon, label, value, tone, highlight }) {
  const tones = {
    emerald: { value: "text-emerald-700", dot: "bg-emerald-500" },
    amber: { value: "text-amber-700", dot: "bg-amber-500" },
  };
  const t = tones[tone] || { value: "text-slate-900", dot: "bg-blue-500" };
  return (
    <div
      data-testid={`metric-${String(label).toLowerCase().replace(/\s+/g, "-")}`}
      className={`rounded-2xl border bg-white ${highlight ? "border-blue-300 bg-blue-50/40 shadow-sm" : "border-slate-200"} px-5 py-5 hover:shadow-sm transition-shadow`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
          <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
          {label}
        </div>
        <Icon className="w-3.5 h-3.5 text-slate-400" />
      </div>
      <p className={`mt-3 font-display text-2xl sm:text-3xl tabular-nums leading-none tracking-tight ${t.value}`}>{value}</p>
    </div>
  );
}

function Novedades({ novedades, onReload }) {
  const [form, setForm] = useState({ titulo: "", resumen: "", contenido: "", importancia: "media", accion_recomendada: "" });
  const [saving, setSaving] = useState(false);
  const [notifying, setNotifying] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/novedades`, form);
      toast.success("Novedad publicada");
      setForm({ titulo: "", resumen: "", contenido: "", importancia: "media", accion_recomendada: "" });
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos publicar");
    } finally {
      setSaving(false);
    }
  };

  const notify = async (id) => {
    if (!window.confirm("¿Enviar email a todos los usuarios Pro activos?")) return;
    setNotifying(id);
    try {
      const { data } = await axios.post(`${API}/admin/novedades/${id}/notify`);
      toast.success(`Enviados ${data.enviados} emails (fallidos ${data.fallidos})`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al notificar");
    } finally {
      setNotifying(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta novedad?")) return;
    try {
      await axios.delete(`${API}/admin/novedades/${id}`);
      toast.success("Eliminada");
      onReload();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="space-y-8">
      <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#1E3A8A]">
          <Plus className="w-3.5 h-3.5" /> Nueva novedad
        </div>
        <input data-testid="novedad-titulo" required placeholder="Título" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} className="w-full h-11 px-4 border-2 border-slate-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none" />
        <textarea required placeholder="Resumen (1-2 frases)" value={form.resumen} onChange={(e) => setForm({ ...form, resumen: e.target.value })} rows={2} className="w-full p-4 border-2 border-slate-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none" />
        <textarea required placeholder="Contenido detallado (Markdown)" value={form.contenido} onChange={(e) => setForm({ ...form, contenido: e.target.value })} rows={6} className="w-full p-4 border-2 border-slate-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none font-mono text-sm" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <select value={form.importancia} onChange={(e) => setForm({ ...form, importancia: e.target.value })} className="h-11 px-3 border-2 border-slate-200 rounded-lg">
            <option value="alta">Importancia alta (dispara email a Pro)</option>
            <option value="media">Importancia media</option>
            <option value="baja">Importancia baja</option>
          </select>
          <input placeholder="Acción recomendada (opcional)" value={form.accion_recomendada} onChange={(e) => setForm({ ...form, accion_recomendada: e.target.value })} className="h-11 px-4 border-2 border-slate-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none" />
        </div>
        <button data-testid="novedad-submit" disabled={saving} type="submit" className="h-11 px-5 rounded-lg bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-semibold disabled:opacity-60 inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> {saving ? "Publicando…" : "Publicar novedad"}
        </button>
      </form>

      <div>
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><Bell className="w-4 h-4" /> Publicadas ({novedades.length})</h3>
        {novedades.length === 0 ? (
          <p className="text-slate-500 text-sm">Aún no hay novedades publicadas.</p>
        ) : (
          <div className="space-y-3">
            {novedades.map((n) => (
              <article key={n.id} className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {n.importancia === "alta" && <AlertTriangle className="w-4 h-4 text-rose-600" />}
                      <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">{n.importancia}</span>
                      <span className="text-xs text-slate-400">· {new Date(n.fecha_publicacion).toLocaleDateString("es-CL")}</span>
                    </div>
                    <h4 className="font-display font-semibold text-slate-900">{n.titulo}</h4>
                    <p className="mt-1 text-sm text-slate-600">{n.resumen}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => notify(n.id)} disabled={notifying === n.id} title="Notificar a Pro" className="p-2 rounded-lg hover:bg-slate-100">
                      {notifying === n.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 text-[#1E3A8A]" />}
                    </button>
                    <button onClick={() => remove(n.id)} title="Eliminar" className="p-2 rounded-lg hover:bg-rose-50 text-rose-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TestimoniosTab({ items, onReload }) {
  const empty = { nombre: "", cargo: "", empresa: "", rubro: "", texto: "", visible: true, orden: 0 };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/testimonials/${editingId}`, form);
        toast.success("Testimonio actualizado");
      } else {
        await axios.post(`${API}/admin/testimonials`, form);
        toast.success("Testimonio creado");
      }
      setForm(empty);
      setEditingId(null);
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({
      nombre: t.nombre || "",
      cargo: t.cargo || "",
      empresa: t.empresa || "",
      rubro: t.rubro || "",
      texto: t.texto || "",
      visible: t.visible !== false,
      orden: t.orden || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleVisible = async (t) => {
    try {
      await axios.put(`${API}/admin/testimonials/${t.id}`, { visible: !t.visible });
      onReload();
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este testimonio?")) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`);
      toast.success("Eliminado");
      onReload();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="space-y-8">
      <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#1E3A8A]">
          <Quote className="w-3.5 h-3.5" /> {editingId ? "Editando testimonio" : "Nuevo testimonio"}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input data-testid="testi-nombre" required placeholder="Nombre (ej: María Pérez)" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="h-11 px-4 border-2 border-slate-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none" />
          <input data-testid="testi-cargo" placeholder="Cargo (ej: Fundadora)" value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} className="h-11 px-4 border-2 border-slate-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none" />
          <input data-testid="testi-empresa" required placeholder="Empresa" value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })} className="h-11 px-4 border-2 border-slate-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none" />
          <input data-testid="testi-rubro" placeholder="Rubro (E-commerce, Salud, Fintech…)" value={form.rubro} onChange={(e) => setForm({ ...form, rubro: e.target.value })} className="h-11 px-4 border-2 border-slate-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none" />
        </div>
        <textarea data-testid="testi-texto" required placeholder="Texto del testimonio (2-4 frases)" value={form.texto} onChange={(e) => setForm({ ...form, texto: e.target.value })} rows={4} className="w-full p-4 border-2 border-slate-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} /> Visible en home y módulo pro
          </label>
          <input type="number" placeholder="Orden (menor = primero)" value={form.orden} onChange={(e) => setForm({ ...form, orden: Number(e.target.value) })} className="h-11 px-4 border-2 border-slate-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none" />
          <div className="flex gap-2 md:justify-end">
            {editingId && (
              <button type="button" onClick={() => { setEditingId(null); setForm(empty); }} className="h-11 px-4 rounded-lg border border-slate-300 text-slate-700 font-medium">Cancelar</button>
            )}
            <button data-testid="testi-submit" disabled={saving} type="submit" className="h-11 px-5 rounded-lg bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-semibold disabled:opacity-60 inline-flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {editingId ? "Guardar cambios" : "Crear testimonio"}
            </button>
          </div>
        </div>
      </form>

      <div>
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><Quote className="w-4 h-4" /> Publicados ({items.length})</h3>
        {items.length === 0 ? (
          <p className="text-slate-500 text-sm">Aún no hay testimonios.</p>
        ) : (
          <div className="space-y-3">
            {items.map((t) => (
              <article key={t.id} data-testid={`admin-testi-${t.id}`} className={`bg-white border rounded-xl p-5 ${t.visible ? "border-slate-200" : "border-slate-300 bg-slate-50"}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="font-display font-semibold text-slate-900">{t.nombre}</span>
                      <span className="text-xs text-slate-500">{[t.cargo, t.empresa].filter(Boolean).join(" · ")}</span>
                      {t.rubro && <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 bg-slate-100 text-slate-600 rounded">{t.rubro}</span>}
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${t.visible ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        {t.visible ? "Visible" : "Oculto"}
                      </span>
                      <span className="text-[10px] text-slate-400">orden: {t.orden}</span>
                    </div>
                <p className="text-sm text-slate-700 italic">&ldquo;{t.texto}&rdquo;</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => toggleVisible(t)} title={t.visible ? "Ocultar" : "Mostrar"} className="p-2 rounded-lg hover:bg-slate-100">
                      {t.visible ? <EyeOff className="w-4 h-4 text-amber-600" /> : <Eye className="w-4 h-4 text-emerald-600" />}
                    </button>
                    <button onClick={() => startEdit(t)} title="Editar" className="p-2 rounded-lg hover:bg-slate-100 text-[#1E3A8A] font-semibold text-xs">Editar</button>
                    <button onClick={() => remove(t.id)} title="Eliminar" className="p-2 rounded-lg hover:bg-rose-50 text-rose-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UsersTable({ users }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <h3 className="font-display font-semibold text-slate-900">Usuarios ({users.length})</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr><th className="text-left p-3">Email</th><th className="text-left p-3">Empresa</th><th className="text-left p-3">Estado</th><th className="text-left p-3">Expira</th><th className="text-left p-3">Último pago</th><th className="text-left p-3">Rol</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="p-3 text-slate-900">{u.email}</td>
                <td className="p-3 text-slate-600">{u.razon_social || "—"}</td>
                <td className="p-3">
                  {u.es_pro ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">Pro</span> : (u.acceso_pro_hasta ? <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">Expirado</span> : <span className="text-slate-400 text-xs">—</span>)}
                </td>
                <td className="p-3 text-slate-500 text-xs">{u.acceso_pro_hasta ? new Date(u.acceso_pro_hasta).toLocaleDateString("es-CL") : "—"}</td>
                <td className="p-3 text-slate-500 text-xs">{u.fecha_ultimo_pago ? new Date(u.fecha_ultimo_pago).toLocaleDateString("es-CL") : "—"}</td>
                <td className="p-3 text-xs">{u.role}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PagosTable({ pagos }) {
  const total = pagos.reduce((s, p) => s + (p.monto_clp || 0), 0);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <h3 className="font-display font-semibold text-slate-900">Pagos ({pagos.length})</h3>
        <span className="text-sm text-slate-600">Total: <strong>${total.toLocaleString("es-CL")} CLP</strong></span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr><th className="text-left p-3">Fecha</th><th className="text-left p-3">Email</th><th className="text-left p-3">Empresa</th><th className="text-left p-3">Monto</th><th className="text-left p-3">Transacción</th><th className="text-left p-3">Días</th></tr>
          </thead>
          <tbody>
            {pagos.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-slate-500">Aún no hay pagos.</td></tr>
            ) : pagos.map((p, i) => (
              <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="p-3 text-slate-500 text-xs">{new Date(p.fecha).toLocaleString("es-CL")}</td>
                <td className="p-3 text-slate-900">{p.email}</td>
                <td className="p-3 text-slate-600">{p.razon_social || "—"}</td>
                <td className="p-3 font-bold text-slate-900">${(p.monto_clp || 0).toLocaleString("es-CL")}</td>
                <td className="p-3 text-xs font-mono text-slate-500">{p.transaccion_id}</td>
                <td className="p-3 text-slate-600">{p.dias_otorgados}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Tab Scans — listado detallado con filtros y stats de drip
// ─────────────────────────────────────────────────────────────
function ScansTable({ scans, filter, onFilter }) {
  if (!scans) return <div className="p-8 text-slate-500">Cargando scans…</div>;
  const { items = [], total = 0, stats_globales: g = {} } = scans;

  const KPI = ({ label, value, accent }) => (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="font-mono text-[10px] tracking-[0.18em] text-slate-500 uppercase">{label}</p>
      <p className={`mt-1 text-xl font-bold ${accent || "text-slate-900"} tabular-nums`}>{value}</p>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="admin-scans-tab">
      {/* KPIs globales */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <KPI label="Scans total" value={g.scans_total ?? 0} />
        <KPI label="Con email" value={g.scans_con_email ?? 0} accent="text-emerald-700" />
        <KPI label="Publicados" value={g.scans_publicados ?? 0} accent="text-blue-700" />
        <KPI label="Drip pendiente" value={g.drips_pendientes ?? 0} accent="text-amber-700" />
        <KPI label="Drip enviado" value={g.drips_enviados ?? 0} accent="text-emerald-700" />
        <KPI label="Drip cancelado" value={g.drips_cancelados ?? 0} accent="text-slate-600" />
        <KPI label="Opt-outs" value={g.opt_outs ?? 0} accent="text-rose-700" />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200">
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            data-testid="scans-filter-email"
            checked={filter.onlyWithEmail}
            onChange={(e) => onFilter({ ...filter, onlyWithEmail: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300"
          />
          Solo con email capturado
        </label>
        <input
          type="text"
          placeholder="Buscar por dominio (ej: empresa.cl)"
          data-testid="scans-filter-domain"
          value={filter.domain}
          onChange={(e) => onFilter({ ...filter, domain: e.target.value })}
          className="h-9 px-3 rounded-lg border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-[#1E3A8A]/20 focus:border-[#1E3A8A] outline-none min-w-[220px]"
        />
        <span className="text-xs text-slate-500 ml-auto">
          Mostrando {items.length} de {total}
        </span>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3 font-semibold">Fecha</th>
              <th className="text-left p-3 font-semibold">URL escaneada</th>
              <th className="text-left p-3 font-semibold">Email</th>
              <th className="text-center p-3 font-semibold">Score</th>
              <th className="text-center p-3 font-semibold">Hallazgos</th>
              <th className="text-center p-3 font-semibold">Multa</th>
              <th className="text-center p-3 font-semibold">Público</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-500">No hay scans con esos filtros.</td></tr>
            ) : items.map((s) => (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`scan-row-${s.id}`}>
                <td className="p-3 text-xs text-slate-500 whitespace-nowrap">
                  {s.creado_en ? new Date(s.creado_en).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" }) : "—"}
                </td>
                <td className="p-3 text-slate-900 max-w-[300px]">
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1E3A8A] hover:underline truncate inline-block max-w-[280px] align-bottom"
                    title={s.url}
                  >
                    {s.url}
                  </a>
                </td>
                <td className="p-3">
                  {s.email ? (
                    <span className="inline-flex items-center gap-1.5">
                      <a href={`mailto:${s.email}`} className="text-slate-900 text-sm">{s.email}</a>
                      {s.marketing_opt_in && (
                        <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-emerald-100 text-emerald-700 uppercase">drip</span>
                      )}
                    </span>
                  ) : <span className="text-slate-400 text-xs italic">— sin email</span>}
                </td>
                <td className="p-3 text-center">
                  <span className={`inline-flex items-center justify-center min-w-8 px-2 py-0.5 rounded font-bold text-xs ${
                    (s.score ?? 0) >= 70 ? "bg-emerald-50 text-emerald-700" :
                    (s.score ?? 0) >= 40 ? "bg-amber-50 text-amber-700" :
                    "bg-rose-50 text-rose-700"
                  }`}>
                    {s.score ?? "—"}
                  </span>
                </td>
                <td className="p-3 text-center text-slate-700">
                  {s.hallazgos_count}
                  {s.hallazgos_alto > 0 && (
                    <span className="ml-1 text-[10px] font-mono text-rose-600">↑{s.hallazgos_alto}</span>
                  )}
                </td>
                <td className="p-3 text-center">
                  {s.categoria_multa ? (
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                      s.categoria_multa === "Gravísima" ? "bg-rose-100 text-rose-700" :
                      s.categoria_multa === "Grave" ? "bg-orange-100 text-orange-700" :
                      "bg-amber-100 text-amber-700"
                    }`}>{s.categoria_multa}</span>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="p-3 text-center">
                  {s.publicado ? (
                    <a
                      href={`/score/${s.public_slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#1E3A8A] underline"
                    >ver</a>
                  ) : <span className="text-slate-300 text-xs">privado</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
